import { CustomFormatter } from '@messageformat/core';

// Hangul (Korean alphabet) syllables occupy a contiguous Unicode block (가 … 힣). Each syllable is
// composed of (초성 lead consonant, 중성 vowel, 종성 final consonant) and its code
// point is: 0xAC00 + (lead * 21 + vowel) * 28 + final. So the final consonant
// (종성/jongseong) — what tells us if there's a batchim — is `(code - 0xAC00) % 28`.
const HANGUL_SYLLABLES_START = 0xac00; // '가', first Hangul syllable
const HANGUL_SYLLABLES_END = 0xd7a3; // '힣', last Hangul syllable
const HANGUL_JONGSEONG_COUNT = 28; // 27 possible final consonants + 1 for "none" (index 0)
const HANGUL_JONGSEONG_RIEUL = 8; // jongseong index of ㄹ (relevant only to the 으로/로 exception)
const LATIN_VOWELS = 'aeiou';
// The (으)로 particle takes its vowel form (로) after a ㄹ ending too, unlike other pairs.
const RIEUL_EXCEPTION_BATCHIM_FORM = '으로';
// Digits are read as Sino-Korean numerals, and each reading ends in a consonant (받침) or a vowel.
// Only the trailing digit matters for any integer (e.g. 21 → 이십'일', 32 → 삼십'이'); a trailing 0 is
// read 영/십/백/천/만, all of which still end in a batchim. '1'/'7'/'8' end in ㄹ (일/칠/팔), so they
// follow the same (으)로 exception as a ㄹ jongseong.
const DIGIT_NO_BATCHIM = new Set(['2', '4', '5', '9']); // 이, 사, 오, 구 — read with a vowel ending
const DIGIT_RIEUL = new Set(['1', '7', '8']); // 일, 칠, 팔 — read with a ㄹ ending

/**
 * MessageFormat custom formatter that appends the correct Korean particle to a
 * value. Korean particles have two mutually exclusive forms depending on whether
 * the preceding word ends in a consonant (받침/batchim) or a vowel, so a
 * translation cannot hard-code one.
 *
 * Usage in a translation: `{name, josa, 을/를}` → `민준을` / `지우를`. The argument
 * lists the forms as `받침형/비받침형` (consonant form first). Works for any pair,
 * e.g. `{value, josa, 이/가}`, `{value, josa, 은/는}`, `{title, josa, 으로/로}`.
 */
export const koreanParticleFormatter: CustomFormatter = (value, _locale, arg) => {
  const word = String(value ?? '');
  const [batchimForm, noBatchimForm] = (arg ?? '').split('/');
  if (!word.trim() || !noBatchimForm) {
    return word; // no value, or a malformed argument — leave it untouched
  }
  return word + (endsWithBatchim(word.trim().slice(-1), batchimForm) ? batchimForm : noBatchimForm);
};

function endsWithBatchim(char: string, batchimForm: string): boolean {
  if (!char) {
    return false;
  }

  const code = char.charCodeAt(0);
  if (code >= HANGUL_SYLLABLES_START && code <= HANGUL_SYLLABLES_END) {
    const jongseong = (code - HANGUL_SYLLABLES_START) % HANGUL_JONGSEONG_COUNT;
    if (jongseong === 0) {
      return false; // ends in a vowel
    }
    if (jongseong === HANGUL_JONGSEONG_RIEUL && batchimForm === RIEUL_EXCEPTION_BATCHIM_FORM) {
      return false; // (으)로 also takes the vowel form after a ㄹ ending
    }
    return true;
  }

  const lowerChar = char.toLowerCase();
  if (lowerChar >= 'a' && lowerChar <= 'z') {
    return !LATIN_VOWELS.includes(lowerChar);
  }

  if (char >= '0' && char <= '9') {
    if (DIGIT_NO_BATCHIM.has(char)) {
      return false; // read with a vowel ending
    }
    if (DIGIT_RIEUL.has(char) && batchimForm === RIEUL_EXCEPTION_BATCHIM_FORM) {
      return false; // ㄹ ending also takes the vowel form for (으)로
    }
    return true;
  }

  return true;
}
